import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { lastValueFrom, Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LocalStorage } from '../../local-storage.class';
import { Routenames } from '../../route-names.enum';

export interface ApiResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

export interface ErrorData {
    error: string;
    errormessage: string;
    data: any;
}

export interface ApiErrorResponse extends ApiResponse<ErrorData> {
    data: ErrorData;
    message: string;
    success: boolean;
    status?: number;
}

@Injectable()
export class ApiService {
    response: Subject<Object> = new Subject();
    response$: Observable<Object>;

    constructor(
        private http: HttpClient,
        private router: Router
    ) {
        this.response$ = this.response.asObservable();
    }

    /**
     * @deprecated Use getCall$
     */
    public getCall<T>(apiUrl: string, params = {}): Promise<T> {
        params = this.buildURLParams(params);

        return lastValueFrom(this.http.get(`${environment.apiEndpoint}${apiUrl}`, { params }))
            .then(response => {
                this.response.next(response);
                return response as T;
            })
            .catch(this.handleError.bind(this));
    }

    public getCallData$<T>(apiUrl: string, params = {}): Observable<T> {
        params = this.buildURLParams(params);

        return this.http.get(`${environment.apiEndpoint}${apiUrl}`, { params }).pipe(
            map((response: ApiResponse<T>) => {
                this.response.next(response);
                return response.data as T;
            }),
            catchError(error => {
                return throwError(this.onError(error));
            })
        );
    }

    public getCall$<T>(apiUrl: string, params = {}): Observable<ApiResponse<T>> {
        params = this.buildURLParams(params);

        return this.http.get(`${environment.apiEndpoint}${apiUrl}`, { params }).pipe(
            map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }),
            catchError(error => {
                return throwError(this.onError(error));
            })
        );
    }

    /**
     * @deprecated Use postCall$
     */
    public postCall<T>(apiUrl: string, params = {}): Promise<T> {
        return lastValueFrom(this.http.post(`${environment.apiEndpoint}${apiUrl}`, params))
            .then(response => {
                this.response.next(response);
                return response as T;
            })
            .catch(this.handleError.bind(this));
    }

    public postCall$<T>(apiUrl: string, params = {}): Observable<ApiResponse<T>> {
        return this.http.post(`${environment.apiEndpoint}${apiUrl}`, params).pipe(
            map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }),
            catchError(error => {
                return throwError(this.onError(error));
            })
        );
    }

    public patchCall$<T>(apiUrl: string, params = {}): Observable<ApiResponse<T>> {
        return this.http.patch(`${environment.apiEndpoint}${apiUrl}`, params).pipe(
            map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }),
            catchError(error => {
                return throwError(this.onError(error));
            })
        );
    }

    /**
     * @deprecated Use patchCall$
     */
    public patchCall<T>(apiUrl: string, params = {}): Promise<T> {
        return lastValueFrom(this.http.patch(`${environment.apiEndpoint}${apiUrl}`, params))
            .then(response => {
                this.response.next(response);
                return response as T;
            })
            .catch(this.handleError.bind(this));
    }

    public putCall$<T>(apiUrl: string, params = {}): Observable<ApiResponse<T>> {
        return this.http.put(`${environment.apiEndpoint}${apiUrl}`, params).pipe(
            map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }),
            catchError(error => {
                return throwError(this.onError(error));
            })
        );
    }

    /**
     * @deprecated Use putCall$
     */
    public putCall<T>(apiUrl: string, params = {}): Promise<T> {
        return lastValueFrom(this.http.put(`${environment.apiEndpoint}${apiUrl}`, params))
            .then(response => {
                this.response.next(response);
                return response as T;
            })
            .catch(this.handleError.bind(this));
    }

    /**
     * @deprecated Use deleteCall$
     */
    public deleteCall<T>(apiUrl: string, params = {}): Promise<T> {
        params = this.buildURLParams(params);

        return lastValueFrom(this.http.delete(`${environment.apiEndpoint}${apiUrl}`, { params }))
            .then(response => {
                this.response.next(response);
                return response as T;
            })
            .catch(this.handleError.bind(this));
    }

    public deleteCall$<T>(apiUrl: string, params = {}): Observable<ApiResponse<T>> {
        params = this.buildURLParams(params);

        return this.http.delete(`${environment.apiEndpoint}${apiUrl}`, { params }).pipe(
            map(response => {
                this.response.next(response);
                return response as ApiResponse<T>;
            }),
            catchError(error => {
                return throwError(this.onError(error));
            })
        );
    }

    // get call with blob as return format
    public getBlobCall(apiUrl: string, params = {}): Observable<any> {
        params = this.buildURLParams(params);
        let headers = new HttpHeaders();
        headers = headers.append('Authorization', `Bearer ${LocalStorage.getUserToken()}`);
        headers = headers.append('Accept', '*');

        return this.http.get(`${environment.apiEndpoint}${apiUrl}`, { params, responseType: 'blob', headers });
    }

    // post call with blob as return format, use this instead of getBlobCall when the payload can get large (eg. long id lists) to avoid "414 Request-URI Too Long"
    public postBlobCall(apiUrl: string, params = {}): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Authorization', `Bearer ${LocalStorage.getUserToken()}`);
        headers = headers.append('Accept', '*');

        return this.http.post(`${environment.apiEndpoint}${apiUrl}`, params, { responseType: 'blob', headers });
    }

    /**
     * Make a request for one file
     * @param apiUrl
     * @param file
     * @param params
     * @param onProgress called with the upload percentage while the file is being sent
     */
    public makeFileRequest$<T>(
        apiUrl: string,
        file: File,
        params = {},
        onProgress?: (percentage: number) => void
    ): Observable<ApiResponse<T>> {
        return new Observable(observer => {
            const formData: FormData = new FormData(),
                xhr: XMLHttpRequest = new XMLHttpRequest();

            formData.append('upload', file, file.name);
            for (const key in params) {
                if (params.hasOwnProperty(key)) {
                    formData.append(key, params[key]);
                }
            }

            if (onProgress) {
                xhr.upload.onprogress = event => {
                    if (event.lengthComputable) {
                        onProgress(Math.round((event.loaded / event.total) * 100));
                    }
                };
            }

            xhr.onreadystatechange = () => {
                console.log(xhr.status);
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    } else {
                        observer.error(this.onError(xhr.response));
                        observer.complete();
                    }
                }
            };

            xhr.open('POST', `${environment.apiEndpoint}${apiUrl}`, true);
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('Authorization', `Bearer ${LocalStorage.getUserToken()}`);
            xhr.send(formData);
        });
    }

    private buildURLParams(data: Object): HttpParams {
        let params = new HttpParams();
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                params = params.set(key, data[key]);
            }
        }
        return params;
    }

    /**
     * @deprecated Use onError
     * @param error
     */
    private handleError(error: any): Promise<any> {
        console.log('An error occured', error);

        const response = {
            success: false,
            message: error,
            data: {},
        } as ApiResponse<any>;

        if (error.status === 0) {
            response.data['errormessage'] = 'No response from server';
        } else {
            response.data['errormessage'] = error.message;
        }

        if (error.status === 401) {
            // Unauthorized
            let route = this.router.routerState.snapshot.root;
            while (route.firstChild) {
                route = route.firstChild;
            }
            const requiresExistingSession = !route.pathFromRoot.some(
                snapshot => snapshot.data['requiresExistingSession'] === false
            );
            if (requiresExistingSession) {
                console.log('API Service error, existing session is unauthorized, redirect to logout');
                this.router.navigate([Routenames.logout], { replaceUrl: true });
            }
        }

        return Promise.reject(response);
    }

    private onError(error: any): ApiErrorResponse {
        const err = { status: error?.status } as ApiErrorResponse;
        if (typeof error['error'] !== 'undefined') {
            if (typeof error['error']['data'] !== 'undefined') {
                err.data = error['error']['data'];
            }
            if (typeof error['error']['message'] !== 'undefined') {
                err.message = error['error']['message'];
            } else if (typeof error['error']['errormessage'] !== 'undefined') {
                err.message = error['error']['errormessage'];
            }
        } else {
            if (typeof error === 'string') {
                try {
                    error = JSON.parse(error);
                } catch (e) {}
                err.data = error;
            } else {
                err.data = new (class implements ErrorData {
                    data = error;
                    error: string;
                    errormessage: string;
                })();
            }
            err.message = 'Some server error';
        }
        console.error(err);
        err.success = false;

        if (error.status === 401) {
            // Unauthorized
            this.router.navigate([Routenames.logout], { replaceUrl: true });
        }

        return err;
    }
}
